import React, { PropsWithChildren, useContext } from "react";
import useSWRImmutable from "swr/immutable";
import { useAuthContext } from "../auth/context";
import { CartLineItem, fetchCartItems, getCartTenant } from "./cart";
import { useWorkContext } from "../work/context";
import { useAlbelliContext } from "../albelli/context";
import { getCareSafeCustomerId } from "../customer/customer";
export type CartContext = {
  cartLineItems: CartLineItem[] | undefined;
};

export const cartContext = React.createContext<CartContext | undefined>(
  undefined,
);

export type CartContextProviderProps = {
  disabled?: boolean;
  locale: string;
};

export function CartContextProvider(
  props: PropsWithChildren<CartContextProviderProps>,
) {
  const authContext = useAuthContext();
  const workContext = useWorkContext();
  const albelliContext = useAlbelliContext();

  const { data, error, isLoading } = useSWRImmutable(
    // https://swr.vercel.app/docs/conditional-fetching#conditional
    () =>
      props.disabled
        ? null
        : buildCartItemsRequest(
            props.locale,
            getCareSafeCustomerId(workContext, albelliContext, authContext),
          ),
    (request) =>
      fetchCartItems(
        request.tenant,
        request.shopperId,
        authContext.authorizationHeader,
      ),
    {
      shouldRetryOnError: false,
    },
  );

  if (isLoading) {
    return <></>;
  } else if (error) {
    throw new Error(`Error fetching cart items: ${error}`);
  }

  return (
    <cartContext.Provider value={{ cartLineItems: data }}>
      {props.children}
    </cartContext.Provider>
  );
}

export function useCartContext() {
  const context = useContext(cartContext);
  if (!context) {
    throw new Error("useCartContext must be used with CartContextProvider");
  }
  return context;
}

function buildCartItemsRequest(locale: string, customerId: string) {
  return {
    tenant: getCartTenant(locale),
    shopperId: customerId,
  };
}
